import React from 'react'
import {
  AccordionCard,
  AccordionCardTab,
} from '@plvs/rally/components/accordionCard/AccordionCard'
import { makeStyles } from '@material-ui/core'
import { useGetEsportBySlugQuery } from '@plvs/graphql'

import { useAutoskipQuery } from '@plvs/utils'
import { Pill } from '@playvs-inc/nexus-components'

import { CreateCSSProperties } from '@material-ui/styles'
import { useEsportContext } from '@plvs/respawn/features/esport/Esport'
import {
  EnrolledPlayerOnTeam,
  EnrollmentLeague,
  SeasonEnrollmentRangeData,
  TeamLeagueSelectFn,
} from '@plvs/respawn/containers/enrollment/types'
import { countTeamsInSeason } from '@plvs/rally/features/enrollment/Row/TeamRow.Select.helper'
import { EnrollmentTeamRow } from './EnrollmentTeamRow'

interface CardProps {
  enrolledPlayersOnTeam: EnrolledPlayerOnTeam[]
  esportId: string
  esportSlug: string
  metaseasonId: string
  onPlayersSelected: TeamLeagueSelectFn
  leagues: EnrollmentLeague[]
  isAdmin: boolean
  seasonSlotExclusionRangeData: SeasonEnrollmentRangeData
  backgroundColor?: string
  addEnrollmentSeasonIds: (seasonIds: string[]) => void
  removeEnrollmentSeasonId: (seasonId: string) => void
  teams: {
    id: string
    name: string | null
    leagues: Array<{
      id: string
      esportId: string | null
    }> | null
    esport: {
      id: string
      slug: string
    }
  }[]
}

const useStyles = makeStyles((theme) => ({
  container: ({
    backgroundColor,
  }: {
    backgroundColor: string | undefined
  }): CreateCSSProperties => ({
    display: 'flex',
    verticalAlign: 'top',
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.BorderLight}`,
    borderRadius: '8px',
    padding: theme.spacing(3),
    backgroundColor: backgroundColor || theme.palette.ColorBackgroundBase,
  }),
  noTeams: {
    color: theme.palette.ColorTextAlt2,
  },
  link: {
    fontWeight: 'bold',
    textDecorationLine: 'none',
    color: theme.palette.ColorTextAlt2,
    borderBottom: `1px solid ${theme.palette.ColorTextAccent}`,
  },
  subsTab: {
    maxHeight: '875px',
  },
  pill: {
    marginRight: theme.spacing(1),
  },
}))

export const EnrollmentEsportCard: React.FC<CardProps> = ({
  leagues,
  esportId,
  esportSlug,
  metaseasonId,
  onPlayersSelected,
  addEnrollmentSeasonIds,
  removeEnrollmentSeasonId,
  enrolledPlayersOnTeam,
  isAdmin,
  seasonSlotExclusionRangeData,
  backgroundColor,
  teams,
}) => {
  const { name } = useEsportContext()

  const classes = useStyles({ backgroundColor })

  const enrollableTeamCount = teams.length

  const { data: esportData } = useAutoskipQuery(useGetEsportBySlugQuery, {
    variables: { slug: esportSlug },
  })

  return (
    <AccordionCard
      badge={
        esportData?.esport?.isPromoted ? (
          <Pill
            className={classes.pill}
            label="New"
            size="large"
            variant="success"
          />
        ) : (
          <></>
        )
      }
      className={classes.container}
      esportSlug={esportSlug}
      hideTabIfSingle
      initialToggle={!!enrollableTeamCount}
      title={name}
      titleDetail={`${enrollableTeamCount} teams ready to be enrolled`}
    >
      <AccordionCardTab tab="Your Teams">
        {teams.map((teamData) => {
          const enrolledLeagueId = teamData?.leagues?.find(
            (league) => league.esportId === esportId
          )?.id
          const enrolledLeague = leagues.find(
            ({ id }) => id === enrolledLeagueId
          )
          const esportLeagues = leagues
            .filter((league) => {
              return league.esport.id === esportId
            })
            .map((l) => {
              return {
                ...l,
                numTeamsInSeasonForSchool: countTeamsInSeason(
                  l,
                  enrolledPlayersOnTeam
                ),
              }
            })
          return (
            <EnrollmentTeamRow
              key={teamData.id}
              addEnrollmentSeasonIds={addEnrollmentSeasonIds}
              enrolledLeague={enrolledLeague}
              enrolledPlayersOnTeam={enrolledPlayersOnTeam}
              isAdmin={isAdmin}
              leagues={esportLeagues}
              metaseasonId={metaseasonId}
              onChange={onPlayersSelected}
              removeEnrollmentSeasonId={removeEnrollmentSeasonId}
              seasonSlotExclusionRangeData={seasonSlotExclusionRangeData}
              teamId={teamData.id}
            />
          )
        })}
      </AccordionCardTab>
    </AccordionCard>
  )
}
